/**
 * Bars.jsx
 * The small pieces every app's figures are drawn from.
 *
 * Each app is measured against itself: a bar is filled relative to the biggest
 * figure in its own list, never the biggest on the screen. Otherwise PeekoType's
 * best day would be a sliver next to TypeShifter's, which says something about
 * TypeShifter and nothing at all about PeekoType.
 *
 * Dependencies: utils/time.js
 */

/** 1,234 — thousands separated, and a missing figure reads as 0 rather than blank. */
export const n = (v) => Number(v ?? 0).toLocaleString();

/** The three figures at the top of an app, so they read as one thought. */
export function Stats({ items }) {
  return (
    <div className="app-stats">
      {items.map((s) => (
        <div className="app-stat" key={s.label}>
          <div className="app-stat__value">{n(s.value)}</div>
          <div className="app-stat__label">{s.label}</div>
          {s.sub && <div className="app-stat__sub">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * A list of bars.
 *
 * @param {object[]} rows {key, label, sub, value, tone}
 * @param {string} empty what to say instead when there is nothing
 * @param {number} [max] the figure a full bar represents; the biggest row by default
 */
export function Bars({ rows, empty, max }) {
  if (!rows || rows.length === 0) return <p className="row__sub">{empty}</p>;

  const top = Math.max(1, max ?? Math.max(...rows.map((r) => Number(r.value) || 0)));

  return (
    <div className="rows">
      {rows.map((r) => (
        <div className="row" key={r.key ?? r.label}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="row__label">{r.label}</div>
            {r.sub && <div className="row__sub">{r.sub}</div>}
            <div className="meter">
              <div
                className={`meter__fill ${r.tone ? `meter__fill--${r.tone}` : ''}`}
                style={{ width: `${Math.min(100, ((Number(r.value) || 0) / top) * 100)}%` }}
              />
            </div>
          </div>
          <div className="row__end">{n(r.value)}</div>
        </div>
      ))}
    </div>
  );
}

/** A plain list with no bar, for short things like pages and referrers. */
export function List({ rows, empty }) {
  if (!rows || rows.length === 0) return <p className="row__sub">{empty}</p>;

  return (
    <div className="rows rows--tight">
      {rows.map((r) => (
        <div className="row" key={r.key ?? r.label}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="row__label">{r.label}</div>
            {r.sub && <div className="row__sub">{r.sub}</div>}
          </div>
          <div className="row__end">{n(r.value)}</div>
        </div>
      ))}
    </div>
  );
}
